/** @author PVCOMBANK */
import React, { useEffect, useState, useMemo } from "react";
import { Paragraph, TextInput, useTheme } from "react-native-paper";
import { RegisterOptions, useForm } from "react-hook-form";
import { Button } from "react-native-paper";
import { QueueSpacing, StackSpacing } from "@components/spacing";
import { Block } from "@block";
import { useDesktopMediaQuery } from "@media-query";
import { useTranslation } from "react-i18next";
import { spacing } from "@styles/spacing";
import { InputSegment, InputText, InputAutoComplete, InputTextCurrency, InputTextMultiline } from "app/shared/components/form/input";
import { StyleSheet } from "react-native";
import { useDispatch, useSelector } from "react-redux";
import {
  getTransferForeignAccountCreditSelector,
  getTransferForeignMakeFeeSelector,
  getTransferForeignStoreSelector,
} from "../../reducer/selectors";
import { Constant } from "app/shared/constant/constant";
import { fetchTransferForeignAccountDetailRequest } from "../../reducer/actions";
import { ACCOUNT, EBINQUIRY } from "app/shared/services/services-model.type";
import { PROFILE_BENE } from "../../../../beneficiaries/utils/bene.utils";
import { nonAccentVietnamese, regexCharacterMarkSpaceDot, replaceEnterKey, replaceSpace } from "app/shared/util/validate";
import {
  amountConfig,
  BRANCH_OTHER,
  getAreaForeign,
  getBankForeign,
  getBranchForeign,
  getListAreaForeign,
  getListBankForeign,
  getListBranchForeign,
  loadBankForeign,
} from "../../../transfer-utils/transfer.utils";
import { decodeBase64 } from "app/shared/util/base64";
import { DataTransferCreate } from "../../../reducer/types";
import { FeeDetailModalNew } from "app/shared/components/modal/make-fee/fee-detail.modal";
import BeneModal from "app/shared/components/modal/bene-account-source/select-bene.modal";
import IconsYellowBanking from "app/shared/components/icons/icon-yellow-banking/IconsYellowBanking";
import FormViewFee from "app/shared/components/form/fee/form-fee";
import { resetListBene } from "app/modules/beneficiaries/reducer/actions";
import { ItemCompleteProps } from "app/shared/components/modal/auto-complete/auto-complete";
import { getLimitRemainSelector } from "app/modules/account-management/reducer/selectors";

export interface FormTransForeignProps {
  onSubmit: Function;
  accountDebit: ACCOUNT;
  onCancel: Function;
  requestMakeFee: Function;
  requestCreate: Function;
  showModal: Function;
  closeModal: Function;
}

enum FormEnum {
  bankCode,
  bankName,
  areaCode,
  areaName,
  branchCode,
  branchName,
  branchOther,
  accountNo,
  accountName,
  amount,
  description,
  feeType,
}

type ValidDataSchemeFormData = {
  [key in keyof typeof FormEnum]: Omit<RegisterOptions, "valueAsNumber" | "valueAsDate" | "setValueAs">;
};

type TransForeignData = {
  bankCode: string;
  bankName: string;
  areaCode: string;
  areaName: string;
  branchCode: string;
  branchName: string;
  branchOther: string;
  accountNo: string;
  accountName: string;
  amount: string;
  description: string;
  feeType: string;
};

const FormTransferForeign = (props: FormTransForeignProps) => {
  const { accountDebit, onSubmit, requestCreate, requestMakeFee, onCancel, showModal, closeModal } = props;
  const { colors } = useTheme();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isDesktop = useDesktopMediaQuery();

  const transferForeignStore = useSelector(getTransferForeignStoreSelector);
  const limitRemainStore = useSelector(getLimitRemainSelector);
  const responseMakeFee = useSelector(getTransferForeignMakeFeeSelector);
  const accountCredit = useSelector(getTransferForeignAccountCreditSelector);

  const [validateAmount, setValidateAmount] = useState({ maxAmount: 0, errorMaxDebit: "" });
  const [bankArray, setBankArray] = useState<ItemCompleteProps[]>([]);
  const [branchArray, setBranchArray] = useState<ItemCompleteProps[]>([]);
  const [areaArray, setAreaArray] = useState<ItemCompleteProps[]>([]);
  const [dataFee, setDataFee] = useState<EBINQUIRY | undefined>(undefined);
  const [accountGetDetail, setAccountGetDetail] = useState<ACCOUNT | undefined>();
  const [isBene, setIsBene] = useState<boolean>(false);
  const [accountBene, setAccountBene] = useState<PROFILE_BENE | undefined>();
  const [isSelectBank, setIsSelectBank] = React.useState<boolean>(false);
  const [isSelectArea, setIsSelectArea] = React.useState<boolean>(false);
  const [isSelectBranch, setIsSelectBranch] = React.useState<boolean>(false);
  const [isBranchOther, setIsBranchOther] = React.useState<boolean>(false);

  // Instance Form Method
  const { control, handleSubmit, getValues, setValue, clearErrors, trigger } = useForm<TransForeignData>({
    defaultValues: {
      bankCode: "",
      bankName: "",
      areaCode: "",
      areaName: "",
      branchCode: "",
      branchName: "",
      branchOther: "",
      accountNo: "",
      accountName: "",
      amount: "",
      description: "",
      feeType: "0",
    },
    mode: "onChange",
  });

  // Lấy danh sách ngân hàng
  const getListBankData = () => {
    const listBank = getListBankForeign();
    setBankArray(listBank);
  };

  // Lấy danh sách khu vực
  const getListAreaData = React.useCallback(() => {
    const listArea = getListAreaForeign();
    setAreaArray(listArea);
  }, []);

  // Lấy danh sách chi nhánh
  const getListBranchData = React.useCallback((bankSearch?: string, areaSearch?: string) => {
    if (bankSearch?.length && areaSearch?.length) {
      const listBranch = getListBranchForeign(bankSearch, areaSearch);
      setBranchArray(listBranch);
      if (listBranch.length) setIsSelectBranch(true);
    }
  }, []);

  // Tạo validate Schema
  const validateSchemeForm: ValidDataSchemeFormData = {
    bankCode: {
      required: {
        value: true,
        message: t("TRANFER_VUI_LONG_CHON_NGAN_HANG"),
      },
    },
    bankName: {
      required: {
        value: false,
        message: t("TRANFER_VUI_LONG_CHON_NGAN_HANG"),
      },
    },
    areaCode: {
      required: {
        value: true,
        message: t("TRANFER_VUI_LONG_CHON_KHU_VUC"),
      },
    },
    areaName: {
      required: {
        value: false,
        message: t("TRANFER_VUI_LONG_CHON_KHU_VUC"),
      },
    },
    branchCode: {
      required: {
        value: true,
        message: t("TRANFER_VUI_LONG_CHON_CHI_NHANH"),
      },
    },
    branchName: {
      required: {
        value: false,
        message: t("TRANFER_VUI_LONG_NHAP_CHI_NHANH_KHAC"),
      },
    },
    branchOther: {
      required: {
        value: isBranchOther,
        message: t("TRANFER_VUI_LONG_NHAP_CHI_NHANH_KHAC"),
      },
    },
    accountNo: {
      required: {
        value: true,
        message: t("TRANFER_THONG_BAO_NHAP_THIEU_TAI_KHOAN"),
      },
    },
    accountName: {
      required: {
        value: true,
        message: t("TRANFER_VUI_LONG_NHAP_TEN_TAI_KHOAN"),
      },
      validate: {
        regexChar: (value) => {
          const regex = regexCharacterMarkSpaceDot();
          if (!regex.test(value)) {
            return t("TRANFER_VUI_LONG_NHAP_TEN_TAI_KHOAN_KHONG_CO_DAU").toString();
          }
          return true;
        },
      },
    },
    amount: {
      required: {
        value: true,
        message: t("TRANFER_THONG_BAO_NHAP_THIEU_SO_TIEN"),
      },
      validate: {
        amountMin: (value) => {
          if (Number(value) < 10000) {
            return t("COMMON_SO_TIEN_KHONG_DUOC_DUOI_10000D").toString();
          }
          return true;
        },
        amountMax: (value) => {
          if (validateAmount?.maxAmount && Number(value) > validateAmount?.maxAmount) {
            return validateAmount?.errorMaxDebit;
          }
          return true;
        },
      },
    },
    description: {
      required: {
        value: true,
        message: t("TRANFER_THONG_BAO_CHUA_NHAP_NOI_DUNG"),
      },
      validate: {
        regexChar: (value) => {
          const regex = regexCharacterMarkSpaceDot();
          if (!regex.test(value)) {
            return t("TRANFER_NOI_DUNG_CHUYEN_TIEN_KHONG_HOP_LE").toString();
          }
          return true;
        },
      },
    },
    feeType: {
      required: {
        value: true,
        message: "",
      },
    },
  };

  const handleRequestAccountCreditDetail = async () => {
    const triggerBank = await trigger("bankCode");
    const triggerAccountNo = await trigger("accountNo");
    if (triggerAccountNo && triggerBank) {
      const bankCode247 = getBankForeign(getValues("bankCode").substr(2, 3))?.ID_247 || "";
      if (bankCode247.length) {
        const dataUpdate: DataTransferCreate = {
          ACCOUNT_DEBIT: accountDebit,
          ACCOUNT_CREDIT: {
            ACCOUNTNO: getValues("accountNo"),
            ACCOUNTTYPE: "10",
            ACCOUNTNAME: getValues("accountName"),
          },
          BANK: { value: bankCode247, label: getValues("bankName") },
          AMOUNT: "10000",
          DESCRIPTION: "INFO",
          FEE_TYPE: "0",
          TYPE: Constant.TRANSFER.TRANSFER_247_TYPE.ACC,
        };
        setAccountGetDetail(dataUpdate.ACCOUNT_CREDIT);
        dispatch(fetchTransferForeignAccountDetailRequest(dataUpdate));
      }
    }
  };

  const handleRequestCreate = (value: TransForeignData): void => {
    const dataUpdate: DataTransferCreate = {
      ACCOUNT_CREDIT: {
        ACCOUNTNO: value.accountNo,
        ACCOUNTTYPE: "10",
        ACCOUNTNAME: value.accountName,
      },
      BANK: { value: value.bankCode, label: value.bankName },
      AREA: { value: value.areaCode, label: value.areaName },
      BRANCH: { value: value.branchCode, label: value.branchName, other: isBranchOther ? value.branchOther : "" },
      AMOUNT: value.amount,
      DESCRIPTION: value.description,
      FEE_TYPE: value.feeType === "0" ? Constant.TRANSFER.TRANSFER_FEE_TYPE.O : Constant.TRANSFER.TRANSFER_FEE_TYPE.I,
      BENE: isBene && accountBene?.ACCOUNT_NUMBER?.length && accountBene?.ACCOUNT_NUMBER === value.accountNo ? accountBene : undefined,
    };
    requestCreate(dataUpdate);
  };

  const handleRequestMakeFee = (value: TransForeignData): void => {
    const dataUpdate: DataTransferCreate = {
      ACCOUNT_CREDIT: {
        ACCOUNTNO: value.accountNo,
        ACCOUNTTYPE: "10",
        ACCOUNTNAME: value.accountName,
      },
      BANK: { value: value.bankCode, label: value.bankName },
      AREA: { value: value.areaCode, label: value.areaName },
      BRANCH: { value: value.branchCode, label: value.branchName },
      AMOUNT: value.amount,
      DESCRIPTION: value.description,
      FEE_TYPE: value.feeType === "0" ? Constant.TRANSFER.TRANSFER_FEE_TYPE.O : Constant.TRANSFER.TRANSFER_FEE_TYPE.I,
    };
    if (dataFee) setDataFee(undefined);
    requestMakeFee(dataUpdate);
  };

  const handlerSelectBene = (_item: PROFILE_BENE) => {
    closeModal();
    const areaCode = _item.BANK_NAME?.substr(0, 2) || "";
    setValue("accountNo", _item.ACCOUNT_NUMBER || "");
    setValue("bankCode", _item.BANK_NAME || "");
    const bankSearch = _item.BANK_NAME?.substr(2, 3);
    setValue("bankName", getBankForeign(bankSearch)?.NODE_DESCRIPTION || "");
    setValue("areaCode", areaCode);
    const areaName = getAreaForeign(areaCode, areaArray)?.label || "";
    setValue("areaName", areaName);
    getListBranchData(_item.BANK_NAME, areaCode);
    const branch = _item.BRANCH_NAME || "";
    const listBranch = getListBranchForeign(_item.BANK_NAME, areaCode);
    if (branch.length) {
      let branchCode: string = branch;
      let branchName: string = "";
      if (branch.indexOf("@@") >= 0) {
        const branchArr = branch.substr(0).split("@@");
        const branchNameCode = decodeBase64(branchArr[0]);
        const branchNameDes = decodeBase64(branchArr[1]);
        if (branchNameCode.indexOf("0&&") >= 0) {
          const branchNameArr = branchNameCode.substr(3).split("&&");
          branchCode = branchNameArr[0];
          branchName = branchNameDes;
          setIsBranchOther(true);
          const branchSearch = getBranchForeign(branchCode, listBranch)?.label || "";
          if (branchSearch.length) {
            branchName = branchSearch;
          } else {
            branchCode = _item.BANK_NAME || "";
            branchName = getBranchForeign(branchCode, listBranch)?.label || "";
          }
        }
      } else {
        const branchSearch = getBranchForeign(branchCode, listBranch)?.label || "";
        if (branchSearch.length) {
          branchName = branchSearch;
        } else {
          branchCode = _item.BANK_NAME || "";
          branchName = getBranchForeign(branchCode, listBranch)?.label || "";
        }
      }
      setValue("branchCode", branchCode);
      setValue("branchName", branchName);
    }
    setIsBene(true);
    setAccountBene(_item);
    handleRequestAccountCreditDetail();
  };

  /// MARK: - Action Handler
  const onPressCreate = () => {
    onSubmit();
    handleSubmit(handleRequestCreate).call(() => {});
  };

  const onPressCancel = () => {
    onCancel();
  };

  const onPressBeneAndAccount = () => {
    dispatch(resetListBene());
    showModal(<BeneModal templateType={Constant.TEMPLATE_TYPE.INTERBANK_TRANSFER} onPressAcceptBene={handlerSelectBene} />);
  };

  const onPressFeeDetail = async () => {
    onSubmit();
    handleSubmit(handleRequestMakeFee).call(() => {});
  };

  const onChangeBank = (bank?: ItemCompleteProps) => {
    setValue("bankName", bank?.label || "");
    if (getValues("areaCode").length) {
      setValue("areaCode", "");
      setValue("areaName", "");
      clearErrors("areaCode");
    }
    if (getValues("branchCode").length) {
      setValue("branchCode", "");
      setValue("branchName", "");
      clearErrors("branchCode");
    }
    if (getValues("accountNo").length) {
      setValue("accountNo", "");
      setValue("accountName", "");
      clearErrors("accountNo");
    }
    if (areaArray.length && bank?.value.length && !isSelectArea) setIsSelectArea(true);
  };

  const onClickSelectArea = () => {
    trigger("bankCode");
  };

  const onChangeAre = (area?: ItemCompleteProps) => {
    setValue("areaName", area?.label || "");
    if (getValues("accountNo").length) {
      setValue("accountNo", "");
      setValue("accountName", "");
    }
    if (getValues("branchCode").length) {
      setValue("branchCode", "");
      setValue("branchName", "");
    }
    getListBranchData(getValues("bankCode"), area?.value);
  };

  const onClickChooseBranch = () => {
    trigger("bankCode");
    trigger("areaCode");
  };

  const onChangeBranch = (branch?: ItemCompleteProps) => {
    setValue("branchName", branch?.label || "");
    if (branch?.value === BRANCH_OTHER) {
      setIsBranchOther(true);
      setValue("branchOther", "");
      clearErrors("branchOther");
    } else {
      setIsBranchOther(false);
    }
  };

  // set thông tin tk nhận
  const onBlurAccountCredit = async () => {
    const bankTrigger = await trigger("bankCode");
    const bankCode = getValues("bankCode");
    const accountNo = getValues("accountNo");
    if (
      bankTrigger &&
      accountBene?.BANK_NAME === bankCode &&
      accountNo.length &&
      accountBene?.ACCOUNT_NUMBER?.length &&
      accountNo === accountBene?.ACCOUNT_NUMBER
    ) {
      setIsBene(true);
    } else {
      setIsBene(false);
    }
    if (bankTrigger && accountNo.length && (accountNo !== accountGetDetail?.ACCOUNTNO || bankCode !== accountGetDetail.BANK_CODE))
      handleRequestAccountCreditDetail();
  };

  const onChangeAccountText = () => {
    if (accountGetDetail) setAccountGetDetail(undefined);
  };

  // fix/replace nội dung chuyển tiền
  const onBlurDescription = () => {
    const value = getValues("description") || "";
    setValue("description", replaceEnterKey(replaceSpace(nonAccentVietnamese(value.trim()))));
    clearErrors("description");
  };

  const onBlurAccountCreditName = () => {
    const value = getValues("accountName") || "";
    setValue("accountName", replaceEnterKey(replaceSpace(nonAccentVietnamese(value.trim()))));
    clearErrors("accountName");
  };

  // Styles Component
  const styles = useMemo(
    () =>
      StyleSheet.create({
        container: { flex: 1 },
        fieldFromText: {
          color: colors.text,
          width: 200,
          fontWeight: "700",
          fontSize: 16,
        },
        buttonContainer: { width: 128, alignSelf: "center", backgroundColor: colors.notification },
        acceptButtonContainer: { width: 128, alignSelf: "center" },
      }),
    [colors],
  );

  const chooseAccountBene = (
    <TextInput.Icon name={() => <IconsYellowBanking name={"danhsachthuhuong"} size={28} />} size={28} onPress={onPressBeneAndAccount} />
  );

  /// MARK: - Child Component
  const FieldForm = (
    <Block>
      <StackSpacing size="medium" />
      <Paragraph style={styles.fieldFromText}>{t("TRANFER_TAI_KHOAN_NHAN")}</Paragraph>
      <StackSpacing size="medium" />
    </Block>
  );

  const FormFee = (
    <>
      <InputSegment
        label={t("TRANFER_BEN_CHIU_PHI")}
        name="feeType"
        control={control}
        values={[t("TRANFER_BEN_CHUYEN"), t("TRANFER_BEN_NHAN")]}
      />
      <FormViewFee onClick={onPressFeeDetail} />
    </>
  );

  const FormsInput = (
    <Block flex={1} bg={colors.backgroundForm} br={12} p={spacing.large}>
      <InputAutoComplete
        title={t("TRANFER_CHON_NGAN_HANG")}
        options={bankArray}
        name="bankCode"
        rules={validateSchemeForm.bankCode}
        control={control}
        label={t("TRANFER_TAI_NGAN_HANG")}
        checkSelect={!isSelectBank}
        onDismiss={onChangeBank}
        showSearch={true}
        typeSearch={Constant.TEMPLATE_TYPE.INTERBANK_TRANSFER}
        showIcon={true}
      />
      <Block spaced row={isDesktop}>
        <Block flex={1}>
          <InputAutoComplete
            title={t("TRANFER_CHON_KHU_VUC")}
            options={areaArray}
            name="areaCode"
            rules={validateSchemeForm.areaCode}
            control={control}
            onClick={onClickSelectArea}
            checkSelect={!isSelectArea}
            onDismiss={onChangeAre}
            label={t("TRANFER_KHU_VUC")}
            showSearch={true}
          />
        </Block>
        <QueueSpacing size="huge" />
        <Block flex={isDesktop ? 2 : 1}>
          <InputAutoComplete
            title={t("TRANFER_CHON_CHI_NHANH")}
            options={branchArray}
            name="branchCode"
            rules={validateSchemeForm.branchCode}
            control={control}
            onDismiss={onChangeBranch}
            label={t("TRANFER_CHI_NHANH")}
            onClick={onClickChooseBranch}
            checkSelect={!isSelectBranch}
            showSearch={true}
          />
        </Block>
      </Block>
      {isBranchOther ? (
        <InputText rules={validateSchemeForm.branchOther} name="branchOther" control={control} label={t("TRANFER_TEN_CHI_NHANH")} />
      ) : (
        <></>
      )}
      <InputText
        rules={validateSchemeForm.accountNo}
        name="accountNo"
        control={control}
        inputProps={{ right: chooseAccountBene }}
        label={t("TRANFER_SO_TAI_KHOAN")}
        onBlur={onBlurAccountCredit}
        onChangedText={onChangeAccountText}
      />
      <InputText
        rules={validateSchemeForm.accountName}
        name="accountName"
        control={control}
        label={t("TRANFER_TEN_TAI_KHOAN")}
        onBlur={onBlurAccountCreditName}
      />
      <InputTextCurrency
        name="amount"
        keyboardType={"numeric"}
        control={control}
        rules={validateSchemeForm.amount}
        label={t("TRANFER_SO_TIEN")}
        rightCurrency={t("COMMON_VND")}
      />
      <InputTextMultiline
        name="description"
        control={control}
        rules={validateSchemeForm.description}
        label={t("TRANFER_NOI_DUNG_CHUYEN_TIEN")}
        note={t("COMMON_NOTE_DESCRIPTION")}
        onBlur={onBlurDescription}
        inputProps={{ multiline: true, maxLength: 100 }}
        rightAffix={true}
      />
      {FormFee}
    </Block>
  );

  const ButtonForm = (
    <Block row middle pv={spacing.huge}>
      <Block w={isDesktop ? 200 : 0} />
      <Button onPress={onPressCancel} mode="contained" style={styles.buttonContainer}>
        {t("COMMON_HUY")}
      </Button>
      <QueueSpacing size="large" />
      <Button mode="contained" style={styles.acceptButtonContainer} onPress={onPressCreate}>
        {t("COMMON_TIEP_TUC")}
      </Button>
    </Block>
  );

  // map nội dung mặc định
  useEffect(() => {
    if (accountDebit.ACCOUNTNAME?.length && !getValues("description").length) {
      const content = accountDebit.ACCOUNTNAME.toUpperCase() + " " + t("TRANFER_DESCRIPTION_CHUYEN_TIEN").toLowerCase();
      setValue("description", replaceEnterKey(replaceSpace(nonAccentVietnamese(content))));
      clearErrors("description");
    }
  }, [accountDebit, clearErrors, getValues, setValue, t]);

  // account credit detail
  useEffect(() => {
    setValue("accountNo", accountCredit?.ACCOUNTNO || "");
    const creditName = accountCredit?.ACCOUNTNAME || "";
    setValue("accountName", replaceEnterKey(replaceSpace(nonAccentVietnamese(creditName.trim()))));
  }, [accountCredit, setValue]);

  useEffect(() => {
    // lấy hạn mức gd ngày/ số dư khả dụng
    const { maxAmount, errorMaxDebit } = amountConfig(accountDebit, limitRemainStore, getValues("accountNo"));
    setValidateAmount({ maxAmount, errorMaxDebit });
  }, [limitRemainStore, accountDebit, accountCredit, getValues]);

  useEffect(() => {
    if (validateAmount?.errorMaxDebit?.length && getValues("amount").length) trigger("amount");
  }, [getValues, trigger, validateAmount]);

  useEffect(() => {
    if (!accountBene?.ACCOUNT_NUMBER?.length && transferForeignStore.dataCreate?.BENE?.ACCOUNT_NUMBER) {
      setAccountBene(transferForeignStore.dataCreate?.BENE);
      if (transferForeignStore.dataCreate?.BENE?.ACCOUNT_NUMBER === getValues("accountNo")) setIsBene(true);
    }
  }, [accountBene, getValues, transferForeignStore]);

  useEffect(() => {
    if (responseMakeFee?.TRANSFER) {
      setDataFee(responseMakeFee.TRANSFER);
    }
  }, [responseMakeFee]);

  // show Phí
  useEffect(() => {
    if (dataFee) {
      showModal(
        <FeeDetailModalNew
          data={dataFee}
          feeType={getValues("feeType") === "0" ? Constant.TRANSFER.TRANSFER_FEE_TYPE.O : Constant.TRANSFER.TRANSFER_FEE_TYPE.I}
        />,
      );
      setDataFee(undefined);
    }
  }, [accountDebit, getValues, dataFee, showModal]);

  // Form khởi tạo
  useEffect(() => {
    // Lấy ds bank/area/branch mặc định
    loadBankForeign().then(() => {
      getListBankData();
      getListAreaData();
    });
  }, [getListAreaData]);

  useEffect(() => {
    if (bankArray.length && !isSelectBank) setIsSelectBank(true);
  }, [bankArray, isSelectBank]);

  return (
    <>
      <Block row={isDesktop ? true : false} pt={spacing.medium}>
        {FieldForm}
        {FormsInput}
      </Block>
      {ButtonForm}
    </>
  );
};

export default FormTransferForeign;
